import React, { useContext, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
// MUI imports
import { Grid2, Collapse, List, ListItem, Toolbar } from "@mui/material";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  LogoutOutlined as LogoutOutlinedIcon,
  ContactSupportOutlined as ContactSupportOutlinedIcon,
} from "@mui/icons-material";
// styles imports
import styles from "./Sidebar.module.scss";
// SVG imports
import { ReactComponent as Logo } from "../../../../GlobalAssets/svg/Logo.svg";
import { ReactComponent as PersonalIcon } from "../../../../GlobalAssets/svg/personalIcon.svg";
import { ReactComponent as ClientIcon } from "../../../../GlobalAssets/svg/clientIcon.svg";
import { ReactComponent as ParentIcon } from "../../../../GlobalAssets/svg/parentIcon.svg";
// local import
import { privateRoutesList } from "../../../../Routes/routeConfig";
import { Actions } from "../../../../redux/constants/actionTypes";
import { getStore } from "../../../../Utils/storeUtils";
import AuthContext from "../../../../store/auth-context";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { clearData } from "../../../../Utils/clearData";
import { buildsV1Sites } from "../../../../Utils/roconConstants";

export default function Sidebar() {
  const [openDropdown, setOpenDropdown] = useState({});
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  const selectedOrg = useSelector(
    (state) => state.organizationsInfo?.selectedOrg,
  );
  const selectedSite = useSelector((state) => state.sitesInfo?.selectedSite);

  const selectedDomain = useSelector((state) => state.sitesInfo.selectedDomain);

  const userDetails = useSelector((state) => state.users?.userDetails);
  const specificPlan = useSelector(
    (state) => state.plansInfo?.specificPlan?.specificPlans,
  );

  const dedicatedHosting = useSelector(
    (state) => state.plansInfo?.specificPlan?.isDedicated === true,
  );

  const basePlan = specificPlan?.find((plan) => plan.basePlan === true);
  const isSingleSite = specificPlan?.find((plan) => plan.isSingleSite === true);

  const location = useLocation();
  const params = useParams();
  let orgId = params.orgId;

  const {
    orgId: siteOrgId,
    siteId,
    sitePage: siteHold,
  } = useSelector((state) => state.sitesInfo?.sitePath) || {};
  const { orgId: orgIds, orgPage: orgHold } =
    useSelector((state) => state.organizationsInfo?.orgPath) || {};
  const { orgId: payOrgId, paymentPage: payHold } =
    useSelector((state) => state.paymentInfo?.paymentPath) || {};

  const handleToggleDropdown = (route) => {
    setOpenDropdown((prevState) => {
      const newState = Object.keys(prevState).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        {},
      );

      newState[route.name] = true;

      const navigateToRoute = (baseRoute, fallbackRoute, condition) => {
        if (condition) {
          navigate(fallbackRoute, { replace: true });
        } else {
          navigate(baseRoute, { replace: true });
        }
      };

      switch (route.name) {
        case "Sites":
          if (
            location.pathname.startsWith("/account/") &&
            location.pathname.includes("sites-overview")
          ) {
            navigate(`/account/${orgId}/sites-overview`);
          } else {
            navigateToRoute(
              `/account/${siteOrgId}/${siteId}/${siteHold}`,
              `/account/${orgId}/sites-overview`,

              !(
                location.pathname.startsWith("/account/") &&
                location.pathname.includes("sites-overview")
              ),
            );
          }
          break;
        case "Dashboard":
          navigateToRoute(
            `/account/${orgIds}/${orgHold}`,
            `/account/${orgId}/dashboard`,
            !(
              location.pathname.startsWith("/account/") &&
              location.pathname.includes("Dashboard")
            ),
          );
          break;
        case "Billing":
          if (specificPlan?.length === 0) {
            navigate(`/account/${orgId}/modify-plan`);
          } else {
            navigateToRoute(
              `/account/${payOrgId}/${payHold}`,
              `/account/${orgId}/billing-overview`,
              !(
                location.pathname.startsWith("/account/") &&
                location.pathname.includes("Billing")
              ),
            );
          }
          break;
        default:
          break;
      }

      return newState;
    });
  };

  useEffect(() => {
    const routeName = getRouteNameFromLocation(location.pathname);
    setOpenDropdown((prevState) => ({ ...prevState, [routeName]: true }));
  }, [location.pathname]);

  const getRouteNameFromLocation = (pathname) => {
    const routeMapping = [
      {
        name: "Sites",
        routes: [
          "site-details",
          "domains-hosted",
          "backups-restores",
          "dev-tools",
          "file-explorer",
          "alerts",
          "site-addons",
          "site-migration",
        ],
      },
      {
        name: "Dashboard",
        routes: ["team-members", "client-organizations", "settings"],
      },
      { name: "Billing", routes: ["plan-usage", "modify-plan", "add-on"] },
    ];

    for (const { name, routes } of routeMapping) {
      if (routes.some((route) => pathname.includes(route))) {
        return name;
      }
    }

    return "";
  };

  const getRedirectPath = (route) => {
    if (route.path) {
      const dynamicPath = `${route.path.replace(":orgId", orgId)}/${
        route.subPath ? route.subPath : route.subRoutes?.path || ""
      }`;

      return dynamicPath;
    }
  };

  const getSubrouteRedirectPath = (route, subroute) => {
    const dynamicOrgRoute = route.path.replace(":orgId", orgId);
    const dynamicSiteRoute = subroute.path.replace(
      ":siteId",
      selectedSite?.siteId || "",
    );
    return `${dynamicOrgRoute}/${dynamicSiteRoute}`;
  };

  // const handleModeChange = (mode) => {
  //   setSelectedMode(mode);
  // };

  // const handleLogout = async (event) => {
  //   const id = showLoadingToast("Logging out... Please wait...");
  //   event.preventDefault();
  //   try {
  //     getStore().dispatch({ type: Actions.LOGOUT_USER });
  //     await authCtx.logout();
  //     setInterval(function () {
  //       window.location.reload();
  //     }, 2000);
  //     showSuccessToast(id, "Logged out successfully!");
  //   } catch (error) {
  //     showErrorToast(id, "Logout failed. Please try again.");
  //   }
  // };

  const handleLogout = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const appConfig = localStorage.getItem("appConfig");
      getStore().dispatch({ type: Actions.LOGOUT_USER });

      if (typeof authCtx.logout === "function") {
        await authCtx.logout();
      }
      if (appConfig && !localStorage.getItem("appConfig")) {
        localStorage.setItem("appConfig", appConfig);
      }

      window.location.href = "/login";
    } catch (error) {
      console.error("Logout error:", error);
      const appConfig = localStorage.getItem("appConfig");
      localStorage.clear();
      if (appConfig) {
        localStorage.setItem("appConfig", appConfig);
      }
      window.location.href = "/login";
    }
  };

  const SupportPage = () => {
    navigate(`/account/${orgId}/support`);
  };

  const urlPath = window.location.pathname;
  const segments = urlPath.split("/");
  const Ind = segments[2];
  const isModifyPlan = urlPath.includes("modify-plan");
  const isSupport = urlPath.includes("support");

  const selectedsiteId = () => {
    getStore().dispatch({
      type: Actions.SET_SELECTED_SITE_DETAILS,
      data: null,
    });
    getStore().dispatch({
      type: Actions.SET_SELECTED_DOMAIN,
      data: null,
    });
  };

  const backSpace = () => {
    clearData();
    navigate("/account/organizations-overview");
  };

  const appConfig = JSON.parse(localStorage.getItem("appConfig") || "{}");
  const appLogo = appConfig.logo;

  return (
    <>
      <ToastContainer autoClose={5000} />
      <div className={styles.FullHeightContainer}>
        <Grid2>
          <Toolbar className={styles.ToolbarLogo} onClick={backSpace}>
            <img src={appLogo} alt="App Logo" className={styles.applogo} />
          </Toolbar>
          <ListItem className={styles.Myspace} onClick={backSpace}>
            <Grid2 className={styles.ListItemIcon}>
              {selectedOrg?.imagePath ? (
                <img
                  src={selectedOrg?.imagePath}
                  alt="img"
                  style={{ width: "30px", height: "30px", borderRadius: "4px" }}
                />
              ) : orgId < 0 ? (
                <PersonalIcon className={styles.nameIcon} />
              ) : selectedOrg?.parentOrgName === null ? (
                <ParentIcon className={styles.nameIcon} />
              ) : (
                <ClientIcon className={styles.nameIcon} />
              )}
            </Grid2>
            <Grid2>
              <Grid2 className={styles.MyspaceText}>
                {orgId > 0 ? "Organization" : "My space"}
              </Grid2>
              <Grid2 className={styles.MyspaceName}>
                {orgId > 0 ? selectedOrg?.name : userDetails?.userName}
              </Grid2>
            </Grid2>
          </ListItem>
          <List className={styles.NavLink}>
            {privateRoutesList
              .filter((route) => {
                if (
                  route.name === "Billing" &&
                  selectedOrg?.parentOrgId != null
                ) {
                  return false;
                }
                if (route.name === "Blueprint" && isSingleSite) {
                  return false;
                }
                // blueprints copy v1 (EFS) storage: only orgs still building v1 sites, or dedicated
                if (
                  route.name === "Blueprint" &&
                  !dedicatedHosting &&
                  !buildsV1Sites(orgId)
                ) {
                  return false;
                }
                return true;
              })

              .map((route, index) => {
                const routeName =
                  route.name === "Billing" && specificPlan?.length === 0
                    ? "Plans"
                    : route.name;

                return (
                  <NavLink
                    to={getRedirectPath(route)}
                    key={index}
                    className={styles.NavLink}
                  >
                    {({ isActive }) => (
                      <>
                        <ListItem
                          button
                          onClick={() => {
                            if (
                              route.name === "Sites" ||
                              route.name === "Dashboard"
                            ) {
                              selectedsiteId();
                            }
                            handleToggleDropdown(route);
                          }}
                          className={`${styles.listItemRoutes} ${
                            isActive ? styles.listItemActiveRoutes : ""
                          } ${
                            isModifyPlan && routeName === "Plans"
                              ? styles.planRoute
                              : ""
                          }`}
                        >
                          <Grid2 className={styles.Flexbox}>
                            <Grid2 className={styles.ListItemIcon}>
                              {isActive
                                ? React.createElement(route.icon, {
                                    className: styles.IconColor,
                                  })
                                : React.createElement(
                                    route.outlinedIcon || route.icon,
                                    { className: styles.IconColor },
                                  )}
                            </Grid2>
                            <Grid2>{routeName}</Grid2>
                          </Grid2>
                          <Grid2>
                            {route.subRoutes &&
                              (route.name !== "Sites" ||
                                selectedSite?.siteId) &&
                              (route.name !== "Dashboard" || Ind !== "-1") &&
                              (route.name !== "Billing" ||
                                specificPlan?.length > 0) &&
                              (openDropdown[route.name] ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              ))}
                          </Grid2>
                        </ListItem>
                        {/* SubRoutes Dropdown */}
                        {route.subRoutes &&
                          (route.name !== "Sites" || selectedSite?.siteId) &&
                          (route.name !== "Dashboard" || Ind !== "-1") &&
                          (route.name !== "Billing" ||
                            specificPlan?.length > 0) && (
                            <Collapse
                              in={openDropdown[route.name]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <List
                                component="div"
                                disablePadding
                                sx={{ marginLeft: "25px" }}
                              >
                                {route.subRoutes
                                  .filter((subRoute) => {
                                    // Site > Add-ons: storage / performance upgrades exist
                                    // for EBS sites only, and billing is not done from a
                                    // child org or a dedicated plan
                                    if (
                                      subRoute.name === "Add-ons" &&
                                      route.name === "Sites" &&
                                      (selectedSite?.ebsSite !== true ||
                                        dedicatedHosting ||
                                        selectedOrg?.parentOrgId != null)
                                    ) {
                                      return false;
                                    }
                                    if (
                                      subRoute.name === "Add-ons" &&
                                      (basePlan?.daysAfterExpiry > 0 ||
                                        (basePlan?.daysAfterExpiry <= 0 &&
                                          basePlan.daysAfterExpiry !== null))
                                    ) {
                                      return false;
                                    }
                                    if (
                                      subRoute.name === "Plan Usage" &&
                                      basePlan?.daysAfterExpiry <= 0 &&
                                      basePlan.daysAfterExpiry !== null
                                    ) {
                                      return false;
                                    }
                                    if (
                                      subRoute.name === "Client Organizations"
                                    ) {
                                      if (isSingleSite) {
                                        return false;
                                      }
                                      return selectedOrg?.parentOrgId === null;
                                    }
                                    if (
                                      subRoute.name === "Domain Hosted" &&
                                      selectedDomain?.environment === "STAGING"
                                    ) {
                                      return false;
                                    }
                                    // Backups and restores are not supported for staging
                                    // environments — they are never taken for one.
                                    if (
                                      subRoute.name === "Backups & Restore" &&
                                      selectedDomain?.environment === "STAGING"
                                    ) {
                                      return false;
                                    }
                                    if (
                                      subRoute.name === "File Explorer" &&
                                      dedicatedHosting
                                    ) {
                                      return false;
                                    }
                                    if (
                                      subRoute.name === "File Explorer" &&
                                      selectedSite?.fileExplorerEnabled === false
                                    ) {
                                      return false;
                                    }
                                    if (
                                      subRoute.name === "Alerts" &&
                                      dedicatedHosting
                                    ) {
                                      return false;
                                    }
                                    return true;
                                  })
                                  .map((subRoute, subIndex) => (
                                    <NavLink
                                      to={getSubrouteRedirectPath(
                                        route,
                                        subRoute,
                                      )}
                                      key={subIndex}
                                      className={styles.NavLink}
                                    >
                                      {({ isActive }) => {
                                        return (
                                          <>
                                            <Grid2
                                              className={styles.lineDisplay}
                                            >
                                              <Grid2 className={styles.line} />
                                              <ListItem
                                                button
                                                className={`${
                                                  styles.listItemSubRoutes
                                                } ${
                                                  isActive
                                                    ? styles.listItemSubActive
                                                    : ""
                                                }`}
                                              >
                                                <Grid2>{subRoute.name}</Grid2>
                                              </ListItem>
                                            </Grid2>
                                          </>
                                        );
                                      }}
                                    </NavLink>
                                  ))}
                              </List>
                            </Collapse>
                          )}
                      </>
                    )}
                  </NavLink>
                );
              })}
          </List>
        </Grid2>
        <Grid2 className={styles.SidebarContainer}>
          <Grid2 className={styles.ContactLogout}>
            <Grid2 className={styles.LogOutFlexBox} onClick={handleLogout}>
              <LogoutOutlinedIcon className={styles.Icon} />
              <Grid2 className={styles.Logout}>Logout</Grid2>
            </Grid2>
            <Grid2
              className={`${styles.Contact} ${
                isSupport ? styles.isSupport : ""
              }`}
              onClick={SupportPage}
            >
              <ContactSupportOutlinedIcon className={styles.Icon} />
            </Grid2>
          </Grid2>
        </Grid2>
      </div>
    </>
  );
}
